/**
 * Hook for account and currency helper functions
 * Extracted from Activity.tsx to improve code organization
 */

import { useMemo } from "react";
import { Account } from "../../../types";
import { formatCurrency } from "../../../utils/transactionUtils";

export interface UseAccountHelpersResult {
  getAccountName: (accountId: string | undefined, storedAccountName?: string) => string;
  getAccountDisplayName: (accountId: string) => string;
  getAccountGroup: (accountId: string | undefined) => string;
  getAccountById: (accountId: string | undefined) => Account | undefined;
}

/**
 * Hook for account-related helper functions
 */
export const useAccountHelpers = (accounts: Account[]): UseAccountHelpersResult => {
  const getAccountById = useMemo(() => {
    return (accountId: string | undefined): Account | undefined => {
      if (!accountId) return undefined;
      return accounts.find((acc) => acc.id === accountId);
    };
  }, [accounts]);

  const getAccountName = useMemo(() => {
    return (accountId: string | undefined, storedAccountName?: string): string => {
      if (!accountId) {
        return storedAccountName ? `${storedAccountName} (Deleted)` : "Unknown Account";
      }
      const account = accounts.find((acc) => acc.id === accountId);
      if (account) {
        return account.accountName;
      }
      // Account not found, use stored name if available
      return storedAccountName ? `${storedAccountName} (Deleted)` : "Unknown Account";
    };
  }, [accounts]);

  const getAccountDisplayName = useMemo(() => {
    return (accountId: string): string => {
      const account = accounts.find((acc) => acc.id === accountId);
      if (account) {
        return `${account.accountName} (${formatCurrency(account.amount || 0, account.currency)})`;
      }
      return "Unknown Account";
    };
  }, [accounts]);

  const getAccountGroup = useMemo(() => {
    return (accountId: string | undefined): string => {
      if (!accountId) return "";
      const account = accounts.find((acc) => acc.id === accountId);
      return account?.group || "";
    };
  }, [accounts]);

  return {
    getAccountName,
    getAccountDisplayName,
    getAccountGroup,
    getAccountById,
  };
};
